import { AttestationType } from '#app/server/db.server.ts'
import { getCollectionProfileURL, getReferenceURL, truncateStringInMiddle } from '#app/utils/misc.tsx'
import InternalLink from '../common/internalLink'
import {
	TypographyHeading,
	TypographyParagraph,
	TypographyLabel,
	TypographyCaps,
} from '../typography'
import {
	LinkIcon,
	EyeIcon,
	StarIcon,
	CommentIcon,
	XIcon,
	CalendarIcon,
	InstagramIcon,
	FarcasterIcon,
	NewsIcon,
	ImageIcon,
	LocationIcon,
	PricetagIcon,
	CollectionIcon,
	CountersignIcon,
	RevokeIcon,
	RollupIcon,
	MenuIcon,
	MoreIcon,
	StarSolidIcon,
	StarRemoveIcon,
} from '../icons'
import { useTheme } from '#app/utils/providers/themeProvider'
import ContentTile from '../content/ContentTile'
import PopupMenu from '../timeline/menu'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { formatMetadataType, getMetadataProfileURL } from '#app/utils/misc.tsx'
import FallbackAvatar from '#/images/avatar11white.png'
import { getMiniTimeDifferenceSinceDate } from '#app/utils/timeUtils'
// Import templates
import Tweet from './templates/Tweet'
import MediaCitation from './templates/MediaCitation'
import Exhibition from './templates/Exhibition'
import RollupExhibition from './templates/RollupExhibition'
import Collection from './templates/Collection'
import Comment from './templates/Comment'
import MediaUpload from './templates/MediaUpload'
import Farcast from './templates/Farcast'
import OffChainSale from './templates/OffChainSale'

import { User } from '#types/loreTypes.tsx'
import Avatar from '../avatars/avatar'
import TypographyLabels from '../typography/TypographyLabel'
import ImagePlaceholder from '#images/image-placeholder.jpg'

// Create an augmented type for our component
type AugmentedAttestationType = Omit<AttestationType, 'properties'> & {
	author?: {
		username?: string
		avatarUrl?: string
	}
	nft?: {
		name?: string
		image?: string
		chain?: string
		description?: string
		imageDetails?: {
			thumbnailUrl?: string
		}
	}
	event?: {
		id?: number,
		eventTitle?: string,
		eventDescription?: string,
		eventLocation?: string,
		dateStart?: string,
		dateEnd?: string,
		eventImageUrl?: string,
		timestamp?: string
	},
	collection?: {
		attestationUid?: string,
		createdAt?: string,
		address?: string,
		template?: {
			name?: string,
			slug?: string,
			version?: string
		},
		references?: {
			nft?: {
				smartContractRange?: {
					[key: string]: Array<{
						contract: string,
						ranges: Array<{
							tokenStart: string,
							tokenEnd: string
						}>
					}>
				},
				smartContractIDs?: {
					[key: string]: Array<{
						contract: string,
						tokenIDs: Array<string>
					}>
				}
			},
			metadataIPFSCID?: string,
			attestationIPFSCID?: string,
			type?: string
		},
		isRevoked?: boolean,
		isReattested?: boolean,
		countersignStatus?: string,
		properties?: {
			name?: string,
			slug?: string,
			description?: string,
			imageUrl?: string,
			artistName?: string,
			nftCount?: number,
			GroupNFTsCID?: string
		},
		signature?: string,
		message?: string
	},
	properties: Record<string, any>
}

type HighlightedAttestationCardProps = {
	attestation: AugmentedAttestationType
	profileUser: User
	onHighlight?: (attestationIPFSCID: string) => Promise<void>
	onRemoveHighlight?: (attestationIPFSCID: string) => Promise<void>
}

export default function HighlightedAttestationCard({
	profileUser,
	attestation,
	onRemoveHighlight = undefined,
}: HighlightedAttestationCardProps) {
	const [theme] = useTheme()
	const isDarkMode = theme === 'dark'
	const { primaryWallet } = useDynamicContext()

	// Helper function to truncate description
	const truncateString = (str: string, maxLength: number) => {
		if (!str) return ''
		if (str.length <= maxLength) return str
		return str.substring(0, maxLength) + '...'
	}

	// Extract the necessary data from the attestation using safe accessors
	const getTitle = () => {
		// Try to get title from different possible locations
		if (typeof attestation.properties === 'object' && attestation.properties) {
			if (attestation.properties.title) {
				return attestation.properties.title
			}
		}
		// Fallback to first line of message
		if (attestation.message) {
			return attestation.message.split('\n')[0]
		}
		return 'Untitled'
	}

	const getDescription = () => {
		if (typeof attestation.properties === 'object' && attestation.properties) {
			if (attestation.properties.description) {
				return attestation.properties.description
			}
		}
		return attestation.message || ''
	}

	const getImageUrl = () => {
		if (typeof attestation.properties === 'object' && attestation.properties) {
			if (attestation.properties.imageUrl) {
				return attestation.properties.imageUrl
			}
		}
		return attestation.nftImage
	}

	// Get type of attestation for displaying appropriate icon
	const getAttestationType = () => {
		const type = attestation.template?.slug || ''

		switch (type) {
			case 'cast':
				return 'CAST'
			case 'comment':
				return 'COMMENT'
			case 'tweet':
				return 'TWEET'
			case 'instagram':
				return 'INSTAGRAM POST'
			case 'mediaCitation':
				return 'MEDIA CITATION'
			case 'media':
				return 'MEDIA UPLOAD'
			case 'proofOfExhibition':
				return 'EXHIBITION'
			case 'offchainSale':
				return 'OFFCHAIN SALE'
			case 'collection':
				return 'COLLECTION'
			case 'countersign':
				return 'COUNTERSIGN'
			case 'revoke':
				return 'REVOKE'
			case 'rollup':
				return 'ROLLUP EXHIBITION'
			default:
				return type.toUpperCase()
		}
	}

	// Get icon based on attestation type
	const getAttestationIcon = () => {
		const type = attestation.template?.slug || ''

		switch (type) {
			case 'comment':
				return <CommentIcon size="small" />
			case 'tweet':
				return <XIcon size="small" />
			case 'cast':
				return <FarcasterIcon size="small" />
			case 'instagram':
				return <InstagramIcon size="small" />
			case 'mediaCitation':
				return <NewsIcon size="small" />
			case 'media':
				return <ImageIcon size="small" />
			case 'proofOfExhibition':
				return <LocationIcon size="small" />
			case 'offchainSale':
				return <PricetagIcon size="small" />
			case 'collection':
				return <CollectionIcon size="small" />
			case 'countersign':
				return <CountersignIcon size="small" />
			case 'revoke':
				return <RevokeIcon size="small" />
			case 'rollup':
				return <RollupIcon size="small" />
			default:
				return <LinkIcon size="small" />
		}
	}

	const title = getTitle()
	const description = getDescription()
	const imageUrl = getImageUrl()
	const attestationType = getAttestationType()
	const attestationIcon = getAttestationIcon()
	const attestationUrl = `/metadataProfile/${attestation.references?.attestationIPFSCID || ''}`
	const dateCreated = attestation.createdAt	? new Date(attestation.createdAt) : new Date()
	const metadataProfileUrl = getMetadataProfileURL(attestation as AttestationType)

	// Get author information
	const getAuthorInfo = () => {
		if (attestation.author?.username) {
			return attestation.author.username
		}

		if (attestation.properties?.authorName) {
			return attestation.properties.authorName
		}

		return '@username'
	}

	const authorInfo = getAuthorInfo()
	const referenceDate = attestation.properties?.publicationDate
		? new Date(attestation.properties.publicationDate).toLocaleDateString(
				'en-US',
				{
					year: 'numeric',
					month: 'long',
					day: 'numeric',
				},	
			)
		: new Date(dateCreated).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})

	// Helper to get NFT data for display in ContentTile
	const getFooterDisplayData = () => {

		// Case for attestations in NFTs
		if (attestation.nft) {
			// Get NFT name
			const name = attestation.nftName || 'Untitled NFT'

			// Get NFT image
			const image = attestation.nftImage || attestation.nftThumbnail

			// Get chain name
			let chainName = ''
			if (attestation.references?.entry?.chain?.name) {
				chainName = attestation.references.entry.chain.name
			} else if (attestation.references?.chain?.name) {
				chainName = attestation.references.chain.name
			} else if (
				attestation.collectionEntries &&
				attestation.collectionEntries.length > 0
			) {
				chainName = attestation.collectionEntries[0].chain
			}

			// Format chain name to capitalize first letter
			if (chainName) {
				chainName =
					chainName.charAt(0).toUpperCase() + chainName.slice(1).toLowerCase()
			}

			// Get attestation URL with getReferenceURL
			const attestationUrl = getReferenceURL(attestation as AttestationType)

			return {
				name,
				image,
				chainName,
				attestationUrl,
				type: 'nft'
			}
		}

		// This case is for any type but attested to a collection. Eg: tweet signed to a collection
		if (attestation.collection) {
			return {
				name: attestation.collection.properties?.name || 'Untitled Collection',
				image: attestation.collection.properties?.imageUrl || '',
				chainName: attestation.collection.references?.nft ? 
					Array.from(new Set([
						...(attestation.collection.references.nft.smartContractRange ? 
							Object.keys(attestation.collection.references.nft.smartContractRange) : []),
						...(attestation.collection.references.nft.smartContractIDs ? 
							Object.keys(attestation.collection.references.nft.smartContractIDs) : [])
					])).map(chain => 
						chain.charAt(0).toUpperCase() + chain.slice(1).toLowerCase()
					).join(', ') : '',
				attestationUrl: getMetadataProfileURL(attestation as AttestationType),
				type: 'collection'
			}
		}


		// This case is for attestations types
		const type = attestation.template?.slug || ''

		switch (type) {
			case 'rollup':
				return {
					name: attestation.event?.eventTitle || attestation.message || 'Attestation',
					image: attestation.event?.eventImageUrl || attestation.nftImage || '',
					chainName: '',
					attestationUrl: getMetadataProfileURL(attestation as AttestationType),
					type: type
				}				
			case 'collection':
				return {
					name: attestation.properties?.name || 'Untitled Collection',
					image: attestation.properties?.web2URL || attestation.properties?.imageUrl || '',
					chainName: attestation.properties?.chains?.map((chain: string) => {
						return chain.charAt(0).toUpperCase() + chain.slice(1).toLowerCase()
					}).join(', ') || '',
					nftCount: attestation.properties?.nftCount || 0,
					attestationUrl: getCollectionProfileURL(attestation as AttestationType),
					type: type
				}
			default:
				return null
		}
	}

	const footerData = getFooterDisplayData()

	// Add this function before the return statement
	const renderTemplate = () => {
		const type = attestation.template?.slug || ''

		switch (type) {
			case 'tweet':
				return <Tweet metadata={attestation} />
			case 'mediaCitation':
				return <MediaCitation metadata={attestation} />
			case 'proofOfExhibition':
				return <Exhibition metadata={attestation} />
			case 'rollup':
				return <RollupExhibition metadata={attestation} />
			case 'collection':
				return <Collection metadata={attestation} />
			case 'comment':
				return <Comment metadata={attestation} />
			case 'media':
				return <MediaUpload metadata={attestation} />
			case 'cast':
				return <Farcast metadata={attestation} />
			case 'offchainSale':
				return <OffChainSale metadata={attestation} />
			default:
				return null
		}
	}

	return (
		<div
			className={`flex flex-col gap-3 overflow-hidden rounded-lg p-4 border border-transparent transition-all duration-300
        ${
					isDarkMode
						? 'bg-grey950 hover:border-uiPrimaryDark'
						: 'bg-uiTertiaryLight hover:border-uiPrimaryLight'
				}`}
		>
			{/* Card header with type icon and actions */}
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-1">
					{attestationIcon && (
						<span className="mr-1 text-textSecondaryLight dark:text-textSecondaryDark">
							{attestationIcon}
						</span>
					)}
					<TypographyCaps size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">
						{attestationType}
					</TypographyCaps>
				</div>
				<div className="flex space-x-3">
					<div className="h-4 w-4 text-textPrimaryLight dark:text-textPrimaryDark dark:hover:text-textPrimaryDark transition-all duration-300">
						<StarSolidIcon
							size="small"
						/>
					</div>
					{/* show time since attestation was created with getElapsedTimeString */}
					<TypographyLabel size='small' additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark">
						{getMiniTimeDifferenceSinceDate(dateCreated)}
					</TypographyLabel>
					<div className="flex h-full w-auto flex-grow items-start justify-end">
							<PopupMenu
								walletAddress={primaryWallet?.address || ''}
								menuButton={<MoreIcon size={16} />}
								metadataEntry={attestation}
								metadataType={attestation.template?.name || 'Attestation'}
								metadataProfileUrl={metadataProfileUrl}
								profileUser={profileUser}
								onRemoveHighlight={
									onRemoveHighlight
										? () =>
												onRemoveHighlight(
													attestation.references?.attestationIPFSCID || '',
												)
										: undefined
								}
							/>
					</div>
				</div>
			</div>

			{/* Card content Depends on Attestation Type */}
			<div className="flex flex-grow flex-col">
				{renderTemplate()}
			</div>

			{/* PFP Username and Date - Only shown when attestation.userId is not the user from the user profile */}
			{profileUser?.id !== attestation.userId && (
				<InternalLink to={`/userProfile/${attestation.userId}`}>
					<div className="flex items-center space-x-1 py-3 border-y border-uiPrimaryLight dark:border-uiTertiaryDark transition-all duration-300">
						<Avatar
							avatar={{
							src: attestation.author?.avatarUrl
								? attestation.author?.avatarUrl
								: attestation.userAvatar
									? attestation.userAvatar
									: FallbackAvatar,
							name:
								attestation.username ? `@${attestation.username}` :
								(attestation.address && attestation.address.includes('.eth'))
									? attestation.address
									: truncateStringInMiddle(attestation.address),
						}}
						size="small"
						isVerified={true}
						additionalClasses="pb-2 md:pb-2"
					/>
					<TypographyLabel size='small' additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark p-1">
						{attestation.username ? `@${attestation.username}` :
							(attestation.address && attestation.address.includes('.eth'))
								? attestation.address
								: truncateStringInMiddle(attestation.address)}
						</TypographyLabel>
					</div>
				</InternalLink>
			)}

			{/* Card footer */}
			{footerData && (
				<div className="flex items-center justify-between">
					<InternalLink
						to={footerData.attestationUrl || ''}
						additionalClasses="w-full border-uiPrimaryLight dark:border-primaryDark"
						>
							<ContentTile
								title={footerData.name}
								description={
									(footerData.nftCount ? `${footerData.nftCount} ${footerData.nftCount === 1 ? 'item' : 'items'}` : '') +
									(footerData.nftCount && footerData.chainName ? ' • ' : '') +
									(footerData.chainName ? `${footerData.chainName}` : '')
								}
								image={footerData.image}
								fallbackImage={ImagePlaceholder}
								variant="collection"
								showBorder={false}
								showCaret={true}
								styledBackground={false}
								elementRight={
									<>
										{/* Media Citation action */}
										{footerData.type === 'mediaCitation' &&
											footerData.attestationUrl && (
												<a
													href="#"
													className="flex items-center space-x-1 text-textSecondaryLight hover:text-textPrimaryLight dark:text-textSecondaryDark dark:hover:text-textPrimaryDark transition-opacity duration-300"
													onClick={e => {
														e.preventDefault()
														e.stopPropagation()
														if (footerData.attestationUrl) {
															window.open(
																footerData.attestationUrl,
																'_blank',
															)
														}
													}}
												>
													<span className="text-xs">Read Article</span>
													<EyeIcon size="small" />
												</a>
											)}

										{/* Tweet action */}
										{footerData.type === 'tweet' &&
											footerData.attestationUrl && (
												<a
													href="#"
													className="flex items-center space-x-1 text-textSecondaryLight hover:text-textPrimaryLight dark:text-textSecondaryDark dark:hover:text-textPrimaryDark transition-opacity duration-300"
													onClick={e => {
														e.preventDefault()
														e.stopPropagation()
														if (footerData.attestationUrl) {
															window.open(
																footerData.attestationUrl,
																'_blank',
															)
														}
													}}
												>
													<span className="text-xs">View Original Tweet</span>
													<EyeIcon className="h-4 w-4" />
												</a>
											)}

										{/* Cast action */}
										{footerData.type === 'cast' &&
											footerData.attestationUrl && (
												<a
													href="#"
													className="flex items-center space-x-1 text-textSecondaryLight hover:text-textPrimaryLight dark:text-textSecondaryDark dark:hover:text-textPrimaryDark transition-opacity duration-300"
													onClick={e => {
														e.preventDefault()
														e.stopPropagation()
														if (footerData.attestationUrl) {
															window.open(
																footerData.attestationUrl,
																'_blank',
															)
														}
													}}
												>
													<span className="text-xs">View Original Cast</span>
													<EyeIcon className="h-4 w-4" />
												</a>
											)}

										{/* Instagram action */}
										{footerData.type === 'instagram' &&
											footerData.attestationUrl && (
												<a
													href="#"
													className="flex items-center space-x-1 text-textSecondaryLight hover:text-textPrimaryLight dark:text-textSecondaryDark dark:hover:text-textPrimaryDark transition-opacity duration-300"
													onClick={e => {
														e.preventDefault()
														e.stopPropagation()
														if (footerData.attestationUrl) {
															window.open(
																footerData.attestationUrl,
																'_blank',
															)
														}
													}}
												>
													<span className="text-xs">View Original Post</span>
													<EyeIcon className="h-4 w-4" />
												</a>
											)}
									</>
								}
							/>
					</InternalLink>
				</div>
			)}
		</div>
	)
}

export function HighlightedAttestationCardSkeleton() {
	const [theme] = useTheme()
	const isDarkMode = theme === 'dark'

	return (
		<div
			className={`flex h-[300px] flex-col gap-3 overflow-hidden rounded-lg p-4
      ${isDarkMode ? 'bg-grey950' : 'bg-uiTertiaryLight'}`}
		>
			{/* Header skeleton */}
			<div className="flex items-center justify-between">
				<div className="bg-gray-700 animate-pulse h-4 w-1/4 rounded"></div>
				<div className="flex space-x-2">
					<div className="bg-gray-700 animate-pulse h-4 w-4 rounded"></div>
					<div className="bg-gray-700 animate-pulse h-4 w-4 rounded"></div>
				</div>
			</div>

			{/* Content skeleton */}
			<div className="flex flex-grow flex-col">
				<div className="bg-gray-700 animate-pulse mb-2 h-6 w-3/4 rounded"></div>
				<div className="bg-gray-700 animate-pulse mb-3 h-4 w-1/3 rounded"></div>
				<div className="bg-gray-700 animate-pulse mb-1 h-4 w-2/3 rounded"></div>
				<div className="bg-gray-700 animate-pulse mb-1 h-4 w-full rounded"></div>
				<div className="bg-gray-700 animate-pulse mb-3 h-4 w-5/6 rounded"></div>

				{/* Image skeleton */}
				<div className="bg-gray-700 animate-pulse mb-3 h-32 w-full rounded"></div>
			</div>

			{/* Footer skeleton */}
			<div className="mt-auto flex items-center justify-between border-t border-grey800">
				<div className="flex items-center space-x-2">
					<div className="bg-gray-700 animate-pulse h-6 w-6 rounded-full"></div>
					<div className="bg-gray-700 animate-pulse h-4 w-32 rounded"></div>
				</div>
				<div className="bg-gray-700 animate-pulse h-4 w-24 rounded"></div>
			</div>
		</div>
	)
}
